import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { STAGE_MAP } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Archive, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ParkRegister() {
  const [parked, setParked] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(() => {
    Promise.all([
      api.get("/ideas", { params: { status: "parked" } }),
      api.get("/ideas", { params: { status: "rejected" } }),
    ]).then(([p, r]) => { setParked(p.data); setRejected(r.data); }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const revive = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/ideas/${id}/revive`); toast.success("Revived to pipeline"); load(); }
    catch { toast.error("Failed to revive"); }
  };

  const Section = ({ title, items, tone }) => (
    <div>
      <h3 className="font-heading font-bold tracking-tight mb-3 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: tone }} /> {title}
        <span className="font-mono text-xs text-muted-foreground">({items.length})</span>
      </h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((i) => (
          <div key={i.id} data-testid={`register-card-${i.id}`} onClick={() => navigate(`/ideas/${i.id}`)}
            className="border border-border bg-card rounded-sm p-3 hover:border-white/25 cursor-pointer transition-colors duration-200">
            <h4 className="font-semibold text-sm">{i.title}</h4>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{i.park_reason}</p>
            <p className="font-mono text-[10px] text-muted-foreground mt-2">Stopped at: {STAGE_MAP[i.current_stage]?.short}</p>
            <Button data-testid={`revive-${i.id}`} onClick={(e) => revive(i.id, e)} size="sm" variant="outline" className="rounded-sm mt-2 h-7 border-border">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Revive
            </Button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground col-span-full">Nothing here.</p>}
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="font-heading text-4xl font-black tracking-tighter flex items-center gap-3">
          <Archive className="w-8 h-8 text-warning" /> Kill / Park Register
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Nothing is lost. Parked and rejected ideas stay retrievable and can be revived.</p>
      </header>
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-8">
          <Section title="Parked" items={parked} tone="#F59E0B" />
          <Section title="Rejected by Council" items={rejected} tone="#FF3B30" />
        </div>
      )}
    </div>
  );
}
